import Config from '../_config/config';
import myAxios from './axiositeachWrapper';

const API_BASE = `${Config.nodeSiteUrl}v1/scorm`;

export interface ScormCourse {
  id: number;
  name: string;
  description: string | null;
  fileName: string | null;
  fileSizeInByte: number | null;
  scormCloudCourseId: string | null;
  importStatus: 'UPLOADING' | 'IMPORTING' | 'READY' | 'ERROR' | null;
  importError: string | null;
  s3Key: string | null;
  version: number | null;
  createdOn: string | null;
  createdBy: string | null;
  usedInModules: number;
  issuedToBatches: number;
  learners: number;
  completions: number;
  avgScore: number | null;
}

export interface ScormRegistrationRow {
  id: number;
  userId: number | null;
  enquiryId: number | null;
  registrationId: string;
  learnerName: string | null;
  completionStatus: string | null;
  successStatus: string | null;
  score: number | null;
  totalSecondsTracked: number | null;
  firstLaunchedOn: string | null;
  lastLaunchedOn: string | null;
  lastSyncedOn: string | null;
}

export interface ScormConnectionInfo {
  ok: boolean;
  serviceUrl?: string;
  appId?: string;
  error?: string;
}

export interface ScormUploadResponse {
  id: number;
  scormCloudCourseId: string;
  importJobId: string;
  importStatus: string;
  s3Key: string | null;
  s3ArchiveError?: string;
}

export interface ScormImportStatusResponse {
  id: number;
  name: string;
  importStatus: 'UPLOADING' | 'IMPORTING' | 'READY' | 'ERROR' | null;
  importError: string | null;
  version: number | null;
  learningStandard?: string;
}

class ScormService {
  testConnection = () => myAxios.get<ScormConnectionInfo>(`${API_BASE}/ping`);

  listCourses = () => myAxios.get<ScormCourse[]>(`${API_BASE}/courses`);

  uploadCourse = (file: File, name: string, description: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    // NB: this axios version's post<T> types the REQUEST body, not the response —
    // type the returned promise instead.
    return myAxios.post(`${API_BASE}/courses`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as unknown as Promise<{ data: ScormUploadResponse }>;
  };

  getImportStatus = (id: number) =>
    myAxios.get<ScormImportStatusResponse>(`${API_BASE}/courses/${id}/import-status`);

  deleteCourse = (id: number, force = false) =>
    myAxios.delete<{ deleted: boolean; cloudDeleteError?: string }>(
      `${API_BASE}/courses/${id}${force ? '?force=true' : ''}`,
    );

  launchCourse = (id: number) =>
    myAxios.post(`${API_BASE}/courses/${id}/launch`, {}) as unknown as Promise<{
      data: { url: string; registrationId: string };
    }>;

  getRegistrations = (id: number, refresh = false) =>
    myAxios.get<ScormRegistrationRow[]>(
      `${API_BASE}/courses/${id}/registrations${refresh ? '?refresh=true' : ''}`,
    );

  // Reset one learner's progress in place (keeps the registration — no new
  // SCORM Cloud registration is consumed). Used to re-demo a course.
  resetRegistration = (courseId: number, userId: number) =>
    myAxios.post(
      `${API_BASE}/courses/${courseId}/registrations/${userId}/reset`,
      {},
    ) as unknown as Promise<{ data: { reset: boolean; registrationId: string } }>;
}

export default new ScormService();
